// Support.jsx
import React, { useState } from 'react';
import Sidebar from '../components/sidebar';
import Header from '../components/header';
import {
    FaPhone,
    FaWhatsapp,
    FaEnvelope,
    FaHeadset,
    FaClock,
    FaCheckCircle,
    FaCopy,
    FaExternalLinkAlt
} from 'react-icons/fa';
import toast, { Toaster } from 'react-hot-toast';

function Support() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState('support');

    // Support contact details
    const supportData = {
        phone: '+917002055429',
        email: 'dev.tamizuahmed@gmail.com',
        whatsapp: 'https://wa.me/917002055429'
    };

    // Handle phone call
    const handleCall = () => {
        window.location.href = `tel:${supportData.phone}`;
        toast.success('Calling support...');
    };

    // Handle WhatsApp
    const handleWhatsApp = () => {
        window.open(supportData.whatsapp, '_blank');
        toast.success('Opening WhatsApp...');
    };

    // Handle Email
    const handleEmail = () => {
        window.location.href = `mailto:${supportData.email}`;
        toast.success('Opening email...');
    };

    // Copy to clipboard
    const copyToClipboard = (text, label) => {
        navigator.clipboard.writeText(text);
        toast.success(`${label} copied to clipboard`);
    };

    return (
        <div className="bg-gray-50 font-sans antialiased min-h-screen">
            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            <Sidebar
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                isOpen={sidebarOpen}
                setIsOpen={setSidebarOpen}
            />

            <div className="main-content lg:ml-[72px] transition-all duration-200">
                <Header
                    currentPage={currentPage}
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                />

                <div className="p-4 sm:p-6 lg:p-8">

                    <div className="mb-6 flex justify-between items-center flex-wrap gap-2">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Support Center</h1>
                            <p className="text-gray-600 text-sm">We're here to help you 24/7</p>
                        </div>
                    </div>

                    {/* Support Options */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        {/* Phone Support */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                            <div className="p-6">
                                <div className="flex flex-col items-center text-center">
                                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                                        <FaPhone className="text-blue-600 text-2xl" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Call Support</h3>
                                    <p className="text-sm text-gray-500 mb-3">Speak directly with our team</p>
                                    <div className="flex items-center gap-2 mb-4">
                                        <span className="text-base font-medium text-gray-700">{supportData.phone}</span>
                                        <button
                                            onClick={() => copyToClipboard(supportData.phone, 'Phone number')}
                                            className="text-gray-400 hover:text-blue-600 transition-colors"
                                        >
                                            <FaCopy className="text-sm" />
                                        </button>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-4">
                                        <FaClock className="text-gray-400" />
                                        <span>Available 24/7</span>
                                    </div>
                                    <button
                                        onClick={handleCall}
                                        className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                                    >
                                        <FaPhone className="text-sm" />
                                        Call Now
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* WhatsApp Support */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                            <div className="p-6">
                                <div className="flex flex-col items-center text-center">
                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                                        <FaWhatsapp className="text-green-600 text-2xl" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-1">WhatsApp</h3>
                                    <p className="text-sm text-gray-500 mb-3">Chat with us on WhatsApp</p>
                                    <div className="flex items-center gap-2 mb-4">
                                        <span className="text-base font-medium text-gray-700">{supportData.phone}</span>
                                        <button
                                            onClick={() => copyToClipboard(supportData.phone, 'WhatsApp number')}
                                            className="text-gray-400 hover:text-green-600 transition-colors"
                                        >
                                            <FaCopy className="text-sm" />
                                        </button>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-4">
                                        <FaCheckCircle className="text-green-500" />
                                        <span>Quick response</span>
                                    </div>
                                    <button
                                        onClick={handleWhatsApp}
                                        className="w-full py-2.5 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                                    >
                                        <FaWhatsapp className="text-sm" />
                                        Chat on WhatsApp
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Email Support */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                            <div className="p-6">
                                <div className="flex flex-col items-center text-center">
                                    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                                        <FaEnvelope className="text-purple-600 text-2xl" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Email Support</h3>
                                    <p className="text-sm text-gray-500 mb-3">Send us an email</p>
                                    <div className="flex items-center gap-2 mb-4">
                                        <span className="text-sm font-medium text-gray-700 truncate max-w-[180px]">
                                            {supportData.email}
                                        </span>
                                        <button
                                            onClick={() => copyToClipboard(supportData.email, 'Email address')}
                                            className="text-gray-400 hover:text-purple-600 transition-colors"
                                        >
                                            <FaCopy className="text-sm" />
                                        </button>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-4">
                                        <FaClock className="text-gray-400" />
                                        <span>Reply within 24 hrs</span>
                                    </div>
                                    <button
                                        onClick={handleEmail}
                                        className="w-full py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                                    >
                                        <FaEnvelope className="text-sm" />
                                        Send Email
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Toaster position="top-right" />
        </div>
    );
}

export default Support;